import React, { useState, ReactElement, isValidElement } from 'react'
import { FixedSizeList as List } from 'react-window'
import AutoSizer from 'react-virtualized-auto-sizer'
import { Autocomplete, Checkbox, Chip, IconButton, Stack, TextField, useTheme } from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import ClearIcon from '@mui/icons-material/Clear'
import { styled } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'
import { EtlValueMapping } from '../../../../shared/model/models'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import { deleteVariable, updateVariable } from '../../../../store/actions'
import { useDebounce } from '../../../../shared/hooks'

type ListboxComponentProps = React.HTMLAttributes<HTMLElement> & {
  children: React.ReactNode
  role: string
}

const ListboxComponent = React.forwardRef<HTMLDivElement, ListboxComponentProps>((props, ref) => {
  const { children, role, ...other } = props
  const items = React.Children.toArray(children) as ReactElement[]
  const itemCount = items.length
  const itemSize = 40
  const listHeight = itemSize * itemCount

  return (
    <div ref={ref}>
      <div {...other}>
        <AutoSizer>
          {({ height, width }) => (
            // @ts-ignore
            <List
              height={height}
              width={width}
              itemSize={itemSize}
              overscanCount={5}
              itemCount={itemCount}
            >
              {({ index, style }) => {
                if (isValidElement(items[index])) {
                  return <div style={style}>{items[index]}</div>
                }
                return null
              }}
            </List>
          )}
        </AutoSizer>
      </div>
    </div>
  )
})

const StyledChip = styled(Chip)({
  '&.MuiChip-root': {
    borderRadius: 4,
    height: 25,
    '& .MuiSvgIcon-root': {
      fontSize: 17,
    },
  },
})

const FsVariable = ({
  mapping,
  containerId,
  etlOriginalValues,
}: {
  mapping: number | null
  containerId: number | null
  etlOriginalValues: string[]
}) => {
  const { etlValueMappings } = useAppSelector((state) => state.currentEtl)
  const charToUpdate = etlValueMappings?.find((char) => char.id === containerId)
  let variable: EtlValueMapping | null = null
  let usedValues: string[] = []
  if (charToUpdate) {
    variable = charToUpdate.mappings.find((map) => map.id === mapping) ?? null
    usedValues = charToUpdate.mappings.flatMap((m) =>
      m.id !== mapping ? m.originalValues ?? [] : [],
    )
  }
  const [uiVariable, setUIVariable] = useState<EtlValueMapping | null>(variable)
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const theme = useTheme()
  const [minRange, setMinRange] = useState<string | null>(null)
  const [maxRange, setMaxRange] = useState<string | null>(null)

  const debouncedUpdateVariableBE = useDebounce(() => {
    if (uiVariable) {
      dispatch(updateVariable({ variable: uiVariable }))
    }
  })

  return (
    uiVariable && (
      <Stack
        borderRadius={2}
        key={containerId}
        p={1}
        gap={1}
        sx={{
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" mr={4} alignItems="center" gap={0.5}>
            <TextField
              size="small"
              key={`${containerId}_label`}
              label={t('variable.select')}
              value={uiVariable.label}
              error={!uiVariable.label}
              sx={{
                backgroundColor: theme.palette.background.paper,
                width: 300,
              }}
              onChange={(e) => {
                setUIVariable({ ...uiVariable, label: e.target.value })
                debouncedUpdateVariableBE()
              }}
            />
          </Stack>
          <Stack justifyContent="flex-end" direction="row" sx={{ width: '100%' }}>
            <Autocomplete
              fullWidth
              size="small"
              multiple
              value={uiVariable.originalValues?.map((v) => `${v}`) ?? []}
              options={[...etlOriginalValues].sort().map((vo) => `${vo}`)}
              disableCloseOnSelect
              getOptionLabel={(option) => `${option}`}
              ChipProps={{ color: 'warning' }}
              disableListWrap
              ListboxComponent={
                ListboxComponent as React.ComponentType<React.HTMLAttributes<HTMLElement>>
              }
              renderOption={(props, option, { selected }) => (
                <li {...props} key={option}>
                  <Checkbox
                    icon={<CheckBoxOutlineBlankIcon />}
                    checkedIcon={<CheckBoxIcon />}
                    style={{ marginRight: 8 }}
                    checked={selected}
                  />
                  {option}
                </li>
              )}
              limitTags={10}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  label={t('values.select')}
                  error={!uiVariable.originalValues || uiVariable.originalValues.length === 0}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <StyledChip
                    {...getTagProps({ index })}
                    variant="outlined"
                    label={`${option}`}
                    deleteIcon={<ClearIcon />}
                  />
                ))
              }
              onChange={(_, newValue: string[]) => {
                setUIVariable({ ...uiVariable, originalValues: newValue })
                debouncedUpdateVariableBE()
              }}
              getOptionDisabled={(option) => usedValues.includes(`${option}`)}
              // PaperComponent={({ children }) => (
              //   <Paper elevation={3} sx={{ p: 2 }}>
              //     <Stack direction="row" justifyContent="space-around" alignItems="center">
              //       <Typography onMouseDown={(event) => event.preventDefault()}>Filter:</Typography>
              //       <Button onMouseDown={(event) => event.preventDefault()} onClick={() => console.log('yaya')}>Test</Button>
              //       <TextField onMouseDown={(event) => {
              //         event.preventDefault()
              //       }} />
              //       <div>
              //         <FsFilterTextField
              //           label="min"
              //           value={minRange}
              //           onChange={(minVal: string) => setMinRange(minVal)}
              //         />
              //       </div>
              //       <FsFilterTextField
              //         label="max"
              //         value={maxRange}
              //         onChange={(maxVal: string) => setMaxRange(maxVal)}
              //       />
              //     </Stack>

              //     {children}
              //   </Paper>
              // )}
            />
            <IconButton
              onClick={() => {
                if (variable) dispatch(deleteVariable({ variable }))
              }}
            >
              <DeleteIcon color="disabled" />
            </IconButton>
          </Stack>
        </Stack>
      </Stack>
    )
  )
}

export default FsVariable
